// Historischer Dienstplan-Import (Wizard, v2)
// Freies Slug-Mapping: für jeden einzigartigen Wert in der 'funktion'-Spalte
// wählt der User einen Typ (Funktion / Funktion+Sprung / Status / Ignorieren)
// und ordnet ihn konkret zu.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, ArrowLeft, ArrowRight, Trash2, AlertTriangle, CheckCircle2, Loader2, Download, Layers, Plus } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { dienstImportApi, dienstApi, wacheApi } from '../lib/api';

// Safe error formatter: FastAPI liefert bei 422 ein Array aus Objekten
// ({type, loc, msg, input, ctx, url}). Direkt an toast.error() übergeben würde
// React-Error #31 auslösen ("object as child").
const formatApiError = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc) ? d.loc.join('.') : '';
        const msg = d?.msg || d?.message || 'Ungültiger Wert';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(' · ');
  }
  return fallback;
};

const STEPS = ['Upload', 'Slugs zuweisen', 'Vorschau', 'Import'];

const CSV_TEMPLATE =
  'name;datum;schicht;funktion\n' +
  'Max Mustermann;02.06.2026;T;StF\n' +
  'Anna Musterfrau;02.06.2026;T;AT/RX\n' +
  'John Doe;02.06.2026;T;S/U\n';

const SLUG_TYPES = [
  { value: 'funktion',        label: 'Funktion' },
  { value: 'funktion_sprung', label: 'Funktion + Sprung' },
  { value: 'status',          label: 'Status (Krank/Urlaub/…)' },
  { value: 'ignorieren',      label: 'Ignorieren' },
];

const STATUS_OPTIONS = [
  { value: 'krank',       label: 'Krank (K)' },
  { value: 'urlaub',      label: 'Urlaub (SU)' },
  { value: 'fortbildung', label: 'Fortbildung (F)' },
  { value: 'frei',        label: 'Frei (--)' },
  { value: 'wandern',     label: 'Wandern (WA)' },
  { value: 'rw',          label: 'RTW-Wandern (RW)' },
  { value: 'zbv',         label: 'ZBV (Zur besonderen Verwendung)' },
  { value: 'tausch',      label: 'Tausch (T)' },
];

const CUSTOM_KUERZEL_MARKER = '__custom__';

// Picker für ein Funktions-Kürzel: freies Textfeld + Vorschlags-Chips für die
// Standard-Funktionen. Kein React-State-Trickery – der Wert ist immer der
// Input-Wert. Das behebt Zeichen-Verdopplung, die beim gemischten
// Select+Input-Pattern auftrat.
function FunktionsPicker({ value, onChange, funktionen, testId }) {
  return (
    <div className="space-y-1">
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Kürzel (z.B. AT, ATF, StF, RTW-Ma …)"
        data-testid={testId}
        autoComplete="off"
        spellCheck={false}
        className="h-9"
      />
      <div className="flex flex-wrap gap-1">
        {funktionen.map((f) => (
          <button
            type="button"
            key={f.kuerzel}
            onClick={() => onChange(f.kuerzel)}
            className={
              'text-[10px] px-1.5 py-0.5 rounded border transition-colors ' +
              (value === f.kuerzel
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted/50 hover:bg-muted border-muted-foreground/20')
            }
            data-testid={`${testId}-chip-${f.kuerzel}`}
          >
            {f.kuerzel}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Eigene Kürzel wie „AT&quot; oder „RTW1&quot; sind ok – sie werden in der Statistik als eigene Zeile geführt.
      </p>
    </div>
  );
}

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2 flex-1">
          <div
            className={
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border ' +
              (i < currentStep
                ? 'bg-primary text-primary-foreground border-primary'
                : i === currentStep
                ? 'bg-primary/10 text-primary border-primary'
                : 'bg-muted text-muted-foreground border-muted-foreground/20')
            }
          >
            {i + 1}
          </div>
          <span className={i === currentStep ? 'font-semibold' : 'text-muted-foreground'}>{label}</span>
          {i < STEPS.length - 1 && <div className="flex-1 h-px bg-muted-foreground/20" />}
        </div>
      ))}
    </div>
  );
}

// -------- Step 1: Upload --------
function UploadStep({ onAnalyze, isLoading, abteilungen, abteilungId, setAbteilungId }) {
  const [file, setFile] = useState(null);

  const handleDownloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dienstplan_import_vorlage.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Datei hochladen</CardTitle>
        <CardDescription>
          CSV oder JSON mit den Spalten: <code>name;datum;schicht;funktion</code>. Optional zusätzlich <code>bemerkung</code>.
          <br />
          Für <b>jeden einzigartigen Wert</b> in der Funktion-Spalte legst du im nächsten Schritt fest, was er bedeutet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="import-abteilung-select" className="text-sm font-semibold">
            Ziel-Wachabteilung <span className="text-red-600">*</span>
          </Label>
          <Select value={abteilungId || ''} onValueChange={setAbteilungId}>
            <SelectTrigger id="import-abteilung-select" data-testid="import-abteilung-select">
              <SelectValue placeholder="Wachabteilung wählen (Pflicht)" />
            </SelectTrigger>
            <SelectContent>
              {(abteilungen || []).map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name || a.nummer || a.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Alle importierten Einteilungen werden dieser Wachabteilung zugeordnet. Nur Mitarbeiter dieser Abteilung
            werden zur Namenserkennung herangezogen (verhindert Mehrdeutigkeiten bei gleichen Nachnamen in mehreren Abteilungen).
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <label className="flex-1 border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors">
            <input
              type="file"
              accept=".csv,.json,text/csv,application/json"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <div className="text-sm font-medium">{file ? file.name : 'CSV oder JSON auswählen'}</div>
            <div className="text-xs text-muted-foreground mt-1">Max. 5 MB</div>
          </label>
          <div className="flex flex-col gap-2">
            <Button type="button" variant="outline" onClick={handleDownloadTemplate} data-testid="import-download-template-btn">
              <Download className="w-4 h-4 mr-2" />
              Vorlage
            </Button>
            <Button
              onClick={() => file && onAnalyze(file)}
              disabled={!file || isLoading || !abteilungId}
              data-testid="import-analyze-btn"
              title={!abteilungId ? 'Bitte zuerst Ziel-Wachabteilung wählen' : undefined}
            >
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Analysieren
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// -------- Slug-Row (Detail-Zeile) --------
function SlugMappingRow({ slug, mapping, fahrzeuge, funktionen, onChange }) {
  const setField = (field, value) => onChange({ ...mapping, [field]: value === '__none__' ? undefined : value });

  const setSprungTeil = (idx, field, value) => {
    const arr = [...(mapping.sprungfunktionen || [])];
    arr[idx] = { ...arr[idx], [field]: value === '__none__' ? undefined : value };
    onChange({ ...mapping, sprungfunktionen: arr });
  };
  const addSprung = () => {
    const arr = [...(mapping.sprungfunktionen || []), { fahrzeug_id: '', funktion_kuerzel: '' }];
    onChange({ ...mapping, sprungfunktionen: arr });
  };
  const removeSprung = (idx) => {
    const arr = (mapping.sprungfunktionen || []).filter((_, i) => i !== idx);
    onChange({ ...mapping, sprungfunktionen: arr });
  };

  return (
    <div className="border rounded-md p-3 space-y-3 bg-card">
      <div className="flex items-center gap-3">
        <Label className="w-32 shrink-0 font-mono text-sm font-semibold">{slug}</Label>
        <Select value={mapping.typ || 'funktion'} onValueChange={(v) => onChange({ typ: v, sprungfunktionen: [] })}>
          <SelectTrigger className="w-56" data-testid={`slug-typ-${slug}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SLUG_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(mapping.typ === 'funktion' || mapping.typ === 'funktion_sprung') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-32">
          <Select value={mapping.fahrzeug_id || '__none__'} onValueChange={(v) => setField('fahrzeug_id', v)}>
            <SelectTrigger data-testid={`slug-fahrzeug-${slug}`}>
              <SelectValue placeholder="Fahrzeug wählen …" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— nicht zuweisen —</SelectItem>
              {fahrzeuge.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.funkrufname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FunktionsPicker
            value={mapping.funktion_kuerzel}
            onChange={(v) => setField('funktion_kuerzel', v || '__none__')}
            funktionen={funktionen}
            testId={`slug-funktion-${slug}`}
          />
        </div>
      )}

      {mapping.typ === 'funktion_sprung' && (
        <div className="pl-32 space-y-2">
          <div className="text-xs text-muted-foreground">Sprungfunktionen:</div>
          {(mapping.sprungfunktionen || []).map((sp, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <Select value={sp.fahrzeug_id || '__none__'} onValueChange={(v) => setSprungTeil(idx, 'fahrzeug_id', v)}>
                <SelectTrigger data-testid={`slug-sprung-fzg-${slug}-${idx}`}>
                  <SelectValue placeholder="Sprung-Fahrzeug …" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— nicht zuweisen —</SelectItem>
                  {fahrzeuge.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.funkrufname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FunktionsPicker
                value={sp.funktion_kuerzel}
                onChange={(v) => setSprungTeil(idx, 'funktion_kuerzel', v || '__none__')}
                funktionen={funktionen}
                testId={`slug-sprung-fn-${slug}-${idx}`}
              />
              <Button variant="ghost" size="sm" onClick={() => removeSprung(idx)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addSprung} data-testid={`slug-add-sprung-${slug}`}>
            <Plus className="w-4 h-4 mr-1" />
            Sprung hinzufügen
          </Button>
        </div>
      )}

      {mapping.typ === 'status' && (
        <div className="pl-32">
          <Select value={mapping.status || '__none__'} onValueChange={(v) => setField('status', v)}>
            <SelectTrigger className="w-64" data-testid={`slug-status-${slug}`}>
              <SelectValue placeholder="Status wählen …" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— nicht zuweisen —</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// -------- Step 2: Mapping --------
function MappingStep({ analyse, fahrzeuge, funktionen, slugMap, setSlugMap, onNext, onBack }) {
  const setSlug = (slug, mapping) => {
    setSlugMap((prev) => ({ ...prev, slugs: { ...prev.slugs, [slug]: mapping } }));
  };

  const isValid = (slug) => {
    const m = slugMap.slugs[slug];
    if (!m || !m.typ) return false;
    if (m.typ === 'ignorieren') return true;
    if (m.typ === 'status') return !!m.status;
    if (m.typ === 'funktion') return !!m.fahrzeug_id && !!m.funktion_kuerzel;
    if (m.typ === 'funktion_sprung') {
      if (!m.fahrzeug_id || !m.funktion_kuerzel) return false;
      const spr = m.sprungfunktionen || [];
      if (spr.length === 0) return false;
      return spr.every((s) => s.fahrzeug_id && s.funktion_kuerzel);
    }
    return false;
  };

  const unmapped = analyse.slugs.filter((s) => !isValid(s));
  const canProceed = unmapped.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Slugs zuweisen</CardTitle>
        <CardDescription>
          Für jeden Wert aus deiner Datei entscheidest du, wie er ins System übernommen wird.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {analyse.slugs.map((slug) => (
            <SlugMappingRow
              key={slug}
              slug={slug}
              mapping={slugMap.slugs[slug] || { typ: 'funktion', sprungfunktionen: [] }}
              fahrzeuge={fahrzeuge}
              funktionen={funktionen}
              onChange={(m) => setSlug(slug, m)}
            />
          ))}
        </div>

        {!canProceed && (
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Noch nicht vollständig zugewiesen: <b>{unmapped.join(', ')}</b>
            </AlertDescription>
          </Alert>
        )}

        <Separator />

        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack} data-testid="mapping-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <Button onClick={onNext} disabled={!canProceed} data-testid="mapping-next-btn">
            Vorschau <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -------- Step 3: Preview --------
function PreviewStep({ preview, onBack, onCommit, isCommitting }) {
  if (!preview) return null;
  const { eintraege, ok_gesamt, fehler_gesamt } = preview;

  const statusBadge = (e) => {
    if (e.ist_krank) return <Badge variant="destructive">Krank</Badge>;
    if (e.ist_urlaub) return <Badge className="bg-cyan-600">Urlaub</Badge>;
    if (e.ist_wandern && e.bemerkung === 'RTW Wandern') return <Badge className="bg-rose-600">RW</Badge>;
    if (e.ist_wandern) return <Badge className="bg-purple-600">Wandern</Badge>;
    if (e.ist_tausch) return <Badge className="bg-blue-600">Tausch</Badge>;
    if (e.ist_frei && e.bemerkung === 'ZBV') return <Badge className="bg-slate-600">ZBV</Badge>;
    if (e.ist_frei) return <Badge variant="outline">{e.bemerkung || 'Frei'}</Badge>;
    return <span>{e.hauptfunktion_kuerzel || '—'}</span>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vorschau</CardTitle>
        <CardDescription>
          <span className="text-emerald-600 font-medium">{ok_gesamt} bereit</span>
          {' • '}
          <span className={fehler_gesamt > 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
            {fehler_gesamt} Warnungen
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea className="h-[420px] rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr className="text-left">
                <th className="p-2 w-10">#</th>
                <th className="p-2">Datum</th>
                <th className="p-2">Dienst</th>
                <th className="p-2">Mitarbeiter</th>
                <th className="p-2">Slug</th>
                <th className="p-2">→ Funktion/Status</th>
                <th className="p-2">Fahrzeug</th>
                <th className="p-2">Springer</th>
                <th className="p-2">Warnungen</th>
              </tr>
            </thead>
            <tbody>
              {eintraege.map((e) => (
                <tr key={e.zeile} className={e.ok ? '' : 'bg-red-50'}>
                  <td className="p-2 font-mono text-xs">{e.zeile}</td>
                  <td className="p-2">{e.datum}</td>
                  <td className="p-2">{e.dienst_art}</td>
                  <td className="p-2">{e.mitarbeiter_name || '—'}</td>
                  <td className="p-2 font-mono text-xs">{e.slug || '—'}</td>
                  <td className="p-2">{statusBadge(e)}</td>
                  <td className="p-2">{e.hauptfahrzeug_name || '—'}</td>
                  <td className="p-2 text-xs">{e.sprung_anzeige.join(', ')}</td>
                  <td className="p-2 text-xs text-red-700">{e.warnungen.join('; ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>

        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack} data-testid="preview-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <Button onClick={onCommit} disabled={isCommitting || ok_gesamt === 0} data-testid="preview-commit-btn">
            {isCommitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {ok_gesamt} Einteilungen importieren
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -------- Batches --------
function BatchesSection({ batches, onDelete, refreshing }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Import-Historie
        </CardTitle>
        <CardDescription>
          Ein Fehlimport lässt sich hier komplett wieder entfernen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {batches.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Imports vorhanden.</p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between border rounded-md p-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.dateiname || `Import ${b.format?.toUpperCase()}`}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(b.erstellt_am).toLocaleString('de-DE')}
                    {b.erstellt_von_name ? ` • ${b.erstellt_von_name}` : ''}
                    {' • '}
                    <span className="font-mono">{b.aktuelle_einteilungen}</span> Einteilungen aktiv
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Badge variant="outline">{b.zeilen_importiert} Zeilen</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={b.aktuelle_einteilungen === 0 || refreshing}
                    onClick={() => onDelete(b)}
                    data-testid={`import-delete-batch-${b.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ========================
// Hauptseite
// ========================
export default function DienstplanImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [rows, setRows] = useState([]);
  const [slugMap, setSlugMap] = useState({ slugs: {}, mitarbeiter: {} });
  const [preview, setPreview] = useState(null);
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [funktionen, setFunktionen] = useState([]);
  const [batches, setBatches] = useState([]);
  const [abteilungen, setAbteilungen] = useState([]);
  const [abteilungId, setAbteilungId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);

  const loadBatches = useCallback(async () => {
    try {
      const res = await dienstImportApi.listBatches();
      setBatches(res.data || []);
    } catch (e) {
      console.error('Load batches error:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [fzgRes, fnRes, abtRes] = await Promise.all([
          dienstApi.getFahrzeuge(),
          dienstApi.getFunktionen(),
          wacheApi.getAbteilungen(),
        ]);
        setFahrzeuge(fzgRes.data || []);
        setFunktionen(fnRes.data || []);
        setAbteilungen(abtRes.data || []);
      } catch (e) {
        console.error('Load base data error:', e);
      }
      loadBatches();
    })();
  }, [loadBatches]);

  const handleAnalyze = async (f) => {
    setIsLoading(true);
    try {
      const res = await dienstImportApi.analyze(f);
      setAnalyse(res.data);
      setRows(res.data.beispiel_zeilen || []);
      setFile(f);

      // Vorbelegung: wenn der Slug exakt einem existierenden Funktions-Kürzel entspricht,
      // Type=Funktion vormerken (Fahrzeug bleibt leer, muss der User wählen).
      const initSlugs = {};
      (res.data.slugs || []).forEach((slug) => {
        const fnMatch = (funktionen || []).find((fn) => fn.kuerzel === slug);
        if (fnMatch) {
          initSlugs[slug] = { typ: 'funktion', funktion_kuerzel: fnMatch.kuerzel, sprungfunktionen: [] };
        } else {
          initSlugs[slug] = { typ: 'funktion', sprungfunktionen: [] };
        }
      });
      setSlugMap({ slugs: initSlugs, mitarbeiter: {} });
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e, 'Analyse fehlgeschlagen'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowPreview = async () => {
    setIsLoading(true);
    try {
      const res = await dienstImportApi.preview({ rows, slug_map: slugMap, abteilung_id: abteilungId || undefined });
      setPreview(res.data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e, 'Vorschau fehlgeschlagen'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommit = async () => {
    setIsCommitting(true);
    try {
      const res = await dienstImportApi.commit({
        rows,
        slug_map: slugMap,
        preview,
        dateiname: file?.name,
        format: file?.name?.toLowerCase().endsWith('.json') ? 'json' : 'csv',
        skip_errors: true,
        abteilung_id: abteilungId || undefined,
      });
      const { zeilen_importiert, zeilen_konflikt = 0, zeilen_uebersprungen = 0 } = res.data;
      let msg = `Import erfolgreich: ${zeilen_importiert} Einteilungen`;
      if (zeilen_konflikt > 0) {
        msg += ` · ${zeilen_konflikt} Zeile(n) übersprungen (bestehende Einteilung hat Vorrang)`;
      }
      if (zeilen_uebersprungen > 0) {
        msg += ` · ${zeilen_uebersprungen} fehlerhaft`;
      }
      if (zeilen_konflikt > 0) {
        toast.warning(msg, { duration: 8000 });
      } else {
        toast.success(msg);
      }
      setStep(3);
      await loadBatches();
    } catch (e) {
      toast.error(formatApiError(e, 'Import fehlgeschlagen'));
    } finally {
      setIsCommitting(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setFile(null);
    setAnalyse(null);
    setRows([]);
    setSlugMap({ slugs: {}, mitarbeiter: {} });
    setPreview(null);
  };

  const handleDeleteBatch = async (b) => {
    if (!window.confirm(`Alle ${b.aktuelle_einteilungen} Einteilungen aus diesem Batch wirklich löschen?`)) return;
    try {
      await dienstImportApi.deleteBatch(b.id);
      toast.success('Batch bereinigt');
      loadBatches();
    } catch (e) {
      toast.error(formatApiError(e, 'Löschen fehlgeschlagen'));
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dienstplan-import-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">Historischer Dienstplan-Import</h1>
            <p className="text-muted-foreground">CSV oder JSON aus einem Alt-System übernehmen – mit freiem Slug-Mapping.</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/einteilung')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Einteilung
          </Button>
        </div>

        <StepIndicator currentStep={step} />

        {step === 0 && (
          <UploadStep
            onAnalyze={handleAnalyze}
            isLoading={isLoading}
            abteilungen={abteilungen}
            abteilungId={abteilungId}
            setAbteilungId={setAbteilungId}
          />
        )}
        {step === 1 && analyse && (
          <MappingStep
            analyse={analyse}
            fahrzeuge={fahrzeuge}
            funktionen={funktionen}
            slugMap={slugMap}
            setSlugMap={setSlugMap}
            onNext={handleShowPreview}
            onBack={() => setStep(0)}
          />
        )}
        {step === 2 && (
          <PreviewStep preview={preview} onBack={() => setStep(1)} onCommit={handleCommit} isCommitting={isCommitting} />
        )}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" /> Import abgeschlossen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Die Einteilungen sind im System. Fehlimport unten wieder komplett entfernen.
              </p>
              <Button onClick={handleReset} data-testid="import-reset-btn">
                Nächsten Import starten
              </Button>
            </CardContent>
          </Card>
        )}

        <BatchesSection batches={batches} onDelete={handleDeleteBatch} refreshing={isLoading} />
      </div>
    </MainLayout>
  );
}
